import { useEffect, useState } from 'react';
import { Archive, BellRing, Bike, Clock, Landmark, LayoutGrid, MapPin, Plus, Save } from 'lucide-react';
import type { ZonaEnvioVista } from '@shared/types';
import { pesosACentavos } from '@shared/centavos';
import { formatPrecio } from '@renderer/utils/formatters';
import {
  CLAVE_DATOS_PAGO,
  CLAVE_MINUTOS_ESPERA_PAGO,
  MINUTOS_ESPERA_PAGO_DEFAULT,
  leerMinutosEspera,
} from '@shared/domicilioEspera';
import {
  CLAVE_POSICION_AVISO,
  CLAVE_SEGUNDOS_AVISO,
  ETIQUETA_POSICION,
  POSICIONES_AVISO,
  POSICION_AVISO_DEFAULT,
  SEGUNDOS_AVISO_DEFAULT,
  leerPosicionAviso,
  leerSegundosAviso,
  CLAVE_TARJETAS_OCULTAS,
  ETIQUETA_TARJETA,
  TARJETAS_DOMICILIO,
  tarjetasVisibles,
  type PosicionAviso,
  type TarjetaDomicilio,
} from '@shared/avisosDomicilio';
import { traducirErrorBackend } from '@renderer/lib/mensajesError';

/**
 * Sprint 76 — Los dos ajustes del flujo a domicilio.
 *
 * **Cuánto se le espera a un cliente su comprobante.** El negocio dijo 15 minutos con la boca
 * chica —*"pues, íjole, es que si el cliente es conocido digamos que lo consideramos"*—, así que
 * el número tenía que quedar donde se pueda cambiar. Un plazo que un sprint dejó escrito en el
 * código es un plazo que nadie puede corregir cuando el negocio cambia; y lo va a cambiar, porque
 * a las nueve de la noche de un viernes no se espera lo mismo que un martes a media tarde.
 *
 * **Los datos para transferir.** Se le dictan al cliente en cada pedido a domicilio. Tenerlos
 * aquí es lo que permite que el mensaje se arme solo, y de paso que el folio vaya en el concepto:
 * sin folio, cuadrar cuatro transferencias del mismo monto se hace a ojo.
 */
export function DomicilioForm() {
  const [minutos, setMinutos] = useState(String(MINUTOS_ESPERA_PAGO_DEFAULT));
  /** Sprint 103 — Cuánto dura en pantalla cada globo de aviso. */
  const [segundosAviso, setSegundosAviso] = useState(String(SEGUNDOS_AVISO_DEFAULT));
  /** Sprint 106 — Cuántos días se guarda el comprobante de una transferencia. */
  const [diasComprobante, setDiasComprobante] = useState('90');
  /** Sprint 108 — En qué esquina aparece el aviso. */
  const [posicionAviso, setPosicionAviso] = useState<PosicionAviso>(POSICION_AVISO_DEFAULT);
  /** Sprint 108 — Qué formas de empezar un pedido usa este negocio. */
  const [tarjetas, setTarjetas] = useState<TarjetaDomicilio[]>(() => tarjetasVisibles(null));
  const [datosPago, setDatosPago] = useState('');
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [guardado, setGuardado] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      const [min, pago, seg] = await Promise.all([
        window.api.config.obtener(CLAVE_MINUTOS_ESPERA_PAGO),
        window.api.config.obtener(CLAVE_DATOS_PAGO),
        window.api.config.obtener(CLAVE_SEGUNDOS_AVISO),
      ]);
      if (min.ok) setMinutos(String(leerMinutosEspera(min.data)));
      if (pago.ok && pago.data) setDatosPago(pago.data);
      if (seg.ok) setSegundosAviso(String(leerSegundosAviso(seg.data)));
      const pos = await window.api.config.obtener(CLAVE_POSICION_AVISO);
      if (pos.ok) setPosicionAviso(leerPosicionAviso(pos.data));
      const tj = await window.api.config.obtener(CLAVE_TARJETAS_OCULTAS);
      if (tj.ok) setTarjetas(tarjetasVisibles(tj.data));
      const neg = await window.api.negocio.obtener();
      if (neg.ok && neg.data) setDiasComprobante(String(neg.data.diasConservarComprobante ?? 90));
      setCargando(false);
    })();
  }, []);

  const guardar = async () => {
    setGuardando(true);
    setError(null);
    setGuardado(false);
    // Se normaliza antes de guardar: lo que se persiste es lo que el sistema va a obedecer, así
    // que un "quince" o un "0" no pueden quedarse en la base esperando a romper el reloj.
    const normalizado = leerMinutosEspera(minutos);
    const r1 = await window.api.config.actualizar(
      CLAVE_MINUTOS_ESPERA_PAGO,
      String(normalizado),
    );
    const r2 = await window.api.config.actualizar(CLAVE_DATOS_PAGO, datosPago.trim().slice(0, 900));
    const segundos = leerSegundosAviso(segundosAviso);
    const r3 = await window.api.config.actualizar(CLAVE_SEGUNDOS_AVISO, String(segundos));
    const r5 = await window.api.config.actualizar(CLAVE_POSICION_AVISO, posicionAviso);
    // Se guardan las OCULTAS y no las visibles: si mañana hay una tarjeta nueva, aparece sola en
    // vez de quedarse invisible porque no estaba en una lista escrita hace meses.
    const ocultas = TARJETAS_DOMICILIO.filter((t) => !tarjetas.includes(t)).join(',');
    const r6 = await window.api.config.actualizar(CLAVE_TARJETAS_OCULTAS, ocultas);
    // Sprint 106 — Este vive en el negocio (no en configuración) porque acompaña a los datos que
    // definen cómo opera: se respalda y se restaura con él.
    const dias = Math.min(3650, Math.max(0, Number(diasComprobante) || 0));
    const r4 = await window.api.negocio.actualizar({ diasConservarComprobante: dias });
    setGuardando(false);
    if (!r1.ok || !r2.ok || !r3.ok || !r4.ok || !r5.ok || !r6.ok) {
      setError(
        traducirErrorBackend(
          (!r1.ok && r1.error) ||
            (!r2.ok && r2.error) ||
            (!r3.ok && r3.error) ||
            (!r4.ok && r4.error) ||
            (!r5.ok && r5.error) ||
            (!r6.ok && r6.error) ||
            '',
        ),
      );
      return;
    }
    setMinutos(String(normalizado));
    setSegundosAviso(String(segundos));
    setDiasComprobante(String(dias));
    setGuardado(true);
  };

  if (cargando) return <div className="text-sm text-slate-500">Cargando…</div>;

  return (
    <div className="max-w-2xl space-y-5">
      <header className="flex items-start gap-3">
        <div className="shrink-0 rounded-full bg-blue-100 p-2">
          <Bike className="h-5 w-5 text-blue-700" />
        </div>
        <div>
          <h2 className="text-lg font-semibold">Pedidos a domicilio</h2>
          <p className="text-sm text-slate-500">
            Cuánto esperas el pago y qué datos le pasas al cliente para transferir.
          </p>
        </div>
      </header>

      <div className="card space-y-2">
        <label className="label flex items-center gap-2" htmlFor="minEspera">
          <Clock className="h-4 w-4 text-slate-500" />
          Cuánto esperar el comprobante
        </label>
        <div className="flex items-center gap-2">
          <input
            id="minEspera"
            className="input w-28"
            inputMode="numeric"
            value={minutos}
            onChange={(e) => setMinutos(e.target.value.replace(/\D/g, '').slice(0, 4))}
          />
          <span className="text-sm text-slate-600">minutos</span>
        </div>
        <p className="text-xs text-slate-500">
          Pasado ese tiempo, el pedido se marca en el tablero y el sistema te pregunta qué hacer:
          esperarlo más, recordárselo o cancelar. <b>Nunca cancela solo</b> — la decisión de
          considerar a un cliente conocido es tuya.
        </p>
      </div>

      {/*
        Sprint 103 — Cuánto dura el globo de aviso.

        Va aquí, con los demás plazos del domicilio, porque es de la misma familia: números que el
        negocio tiene que poder cambiar sin pedirle nada a nadie. Ocho segundos alcanzan para leer
        dos renglones; quien atiende sola quizá quiera más, y quien tiene el tablero al lado, menos.
      */}
      <div className="card space-y-2">
        <label className="label flex items-center gap-2" htmlFor="segAviso">
          <BellRing className="h-4 w-4 text-slate-500" />
          Cuánto dura el aviso en pantalla
        </label>
        <div className="flex items-center gap-2">
          <input
            id="segAviso"
            className="input w-28"
            inputMode="numeric"
            value={segundosAviso}
            onChange={(e) => setSegundosAviso(e.target.value.replace(/\D/g, '').slice(0, 3))}
          />
          <span className="text-sm text-slate-600">segundos</span>
        </div>
        <p className="text-xs text-slate-500">
          Cuando entra un pedido del enlace, un cliente avisa que transfirió o confirma que
          recibió, aparece un aviso <b>estés en la sección que estés</b>. Se va solo, no tapa lo que
          estés haciendo, y al tocarlo te lleva al pedido. Entre 2 y 60 segundos.
        </p>

        {/*
          Sprint 108 — Dónde aparece.

          Reporte del dueño del producto la primera vez que lo probó: *"ni lo vi, porque llegó por
          la parte inferior derecha y yo estaba mirando al centro y arriba"*. Un aviso fuera del
          campo visual no es un aviso — y de paso gasta la confianza en el mecanismo.

          El default pasó a **arriba al centro**, que es donde vive la vista cuando se trabaja en el
          Dashboard. Se puede mover porque eso depende de cada pantalla y de dónde tenga cada quien
          puesto el WhatsApp.
        */}
        <div className="mt-3">
          <label className="label" htmlFor="posAviso">
            ¿Dónde quieres que aparezca?
          </label>
          <select
            id="posAviso"
            className="input w-full"
            value={posicionAviso}
            onChange={(e) => setPosicionAviso(e.target.value as PosicionAviso)}
          >
            {POSICIONES_AVISO.map((p) => (
              <option key={p} value={p}>
                {ETIQUETA_POSICION[p]}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-slate-500">
            Ponlo donde ya estés mirando. Si trabajas con los botones grandes del inicio, arriba al
            centro es lo que se ve sin buscarlo.
          </p>
        </div>
      </div>

      {/*
        Sprint 106 — Cuánto se guarda el comprobante.

        Decisión del negocio: *"conservar comprobante transferencia [ ] días… y si me apuras hasta
        descargar el ZIP antes de purgar"*. Los comprobantes viven en la base desde este sprint, así
        que ocupan espacio y viajan en cada respaldo: por eso caducan. **Nada se borra solo** — la
        purga se pide y exporta un ZIP antes.
      */}
      {/*
        Sprint 108 — Qué formas de empezar un pedido usa este negocio.

        *"Sería genial poder apagar los cards (configurable si ya no tiene uso)"*. Apagar **oculta
        la tarjeta, no cierra el camino**: un pedido dictado por teléfono se sigue pudiendo capturar
        desde el POS. Y no se pueden apagar las tres — un tablero sin ninguna forma de empezar
        parece roto, no configurado.
      */}
      <div className="card space-y-2">
        <div className="label flex items-center gap-2">
          <LayoutGrid className="h-4 w-4 text-slate-500" />
          Cómo empiezan tus pedidos a domicilio
        </div>
        <div className="space-y-2">
          {TARJETAS_DOMICILIO.map((t) => {
            const activa = tarjetas.includes(t);
            const ultima = activa && tarjetas.length === 1;
            return (
              <label
                key={t}
                className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 ${
                  activa ? 'border-slate-300 bg-white' : 'border-slate-200 bg-slate-50 opacity-70'
                }`}
              >
                <input
                  type="checkbox"
                  className="mt-0.5 h-5 w-5"
                  checked={activa}
                  disabled={ultima}
                  onChange={(e) =>
                    setTarjetas((prev) =>
                      e.target.checked
                        ? TARJETAS_DOMICILIO.filter((x) => prev.includes(x) || x === t)
                        : prev.filter((x) => x !== t),
                    )
                  }
                />
                <span className="text-sm">
                  <span className="font-medium">{ETIQUETA_TARJETA[t]}</span>
                  {ultima && (
                    <span className="mt-0.5 block text-xs text-amber-700">
                      No puedes apagar la última: el tablero se quedaría sin forma de empezar.
                    </span>
                  )}
                </span>
              </label>
            );
          })}
        </div>
        <p className="text-xs text-slate-500">
          Apaga las que no uses. <b>No pierdes la función</b>: sólo deja de ocupar espacio en el
          tablero. Un pedido dictado por teléfono lo sigues pudiendo capturar desde el punto de
          venta.
        </p>
      </div>

      <div className="card space-y-2">
        <label className="label flex items-center gap-2" htmlFor="diasComp">
          <Archive className="h-4 w-4 text-slate-500" />
          Conservar el comprobante de transferencia
        </label>
        <div className="flex items-center gap-2">
          <input
            id="diasComp"
            className="input w-28"
            inputMode="numeric"
            value={diasComprobante}
            onChange={(e) => setDiasComprobante(e.target.value.replace(/\D/g, '').slice(0, 4))}
          />
          <span className="text-sm text-slate-600">días</span>
        </div>
        <p className="text-xs text-slate-500">
          Pasado ese tiempo el comprobante <b>puede</b> purgarse, pero nunca se borra solo: hay que
          pedirlo, y el sistema te lo exporta en un ZIP antes de tirarlo. Escribe <b>0</b> para
          guardarlos para siempre. Los pedidos y sus pagos no se tocan nunca.
        </p>
      </div>

      <div className="card space-y-2">
        <label className="label flex items-center gap-2" htmlFor="datosPago">
          <Landmark className="h-4 w-4 text-slate-500" />
          Datos para transferir
        </label>
        <textarea
          id="datosPago"
          className="input min-h-[110px] font-mono text-sm"
          value={datosPago}
          onChange={(e) => setDatosPago(e.target.value)}
          maxLength={900}
          placeholder={'BBVA · Nombre del negocio\nCLABE 000 000 000 000 000 000'}
        />
        <p className="text-xs text-slate-500">
          Es lo que se copia junto con el total cuando le pides el pago al cliente. El número de
          pedido se agrega solo como concepto, que es lo que después permite saber qué
          transferencia corresponde a qué pedido.
        </p>
      </div>

      {error && (
        <div className="error-text" role="alert">
          {error}
        </div>
      )}

      <div className="flex items-center gap-3">
        <button onClick={guardar} className="btn-primary min-h-[44px]" disabled={guardando}>
          <Save className="h-4 w-4" />
          {guardando ? 'Guardando…' : 'Guardar'}
        </button>
        {guardado && <span className="text-sm text-emerald-700">✓ Guardado</span>}
      </div>

      <ZonasEnvioSection />
    </div>
  );
}

/**
 * Sprint 77 — Las zonas de reparto con su tarifa.
 *
 * El negocio cobra por zona —*"Galaxia, Tabasco 2000 desde mi negocio son 50 pesos"*— y en la
 * misma frase puso su regla: *"siempre por regla prefiero cotizar"*. Por eso esto no aplica nada
 * solo: es la lista de atajos que aparece al cotizar un envío.
 *
 * Se captura a mano a propósito. El sistema también aprende de los envíos anteriores, pero el
 * aprendizaje no sirve el primer día: un negocio sin histórico —o el cliente alpha, hoy en modo
 * práctica— vería la sugerencia vacía durante semanas y concluiría que la función no funciona.
 */
function ZonasEnvioSection() {
  const [zonas, setZonas] = useState<ZonaEnvioVista[]>([]);
  const [cargando, setCargando] = useState(true);
  const [nombre, setNombre] = useState('');
  const [tarifa, setTarifa] = useState('');
  /**
   * Sprint 111 — Hasta cuántos kilómetros llega esta tarifa.
   *
   * Se pide **un solo número** —"hasta X km"— y no dos. El "desde" se calcula solo encadenando con
   * la zona anterior, porque en el piso nadie piensa "de 3 a 6": piensa *"hasta 3 km cobro $40,
   * hasta 6 cobro $50, más lejos cotizo"*. Pedir dos números por renglón es cómo esta sección
   * llevaba meses vacía.
   */
  const [hastaKm, setHastaKm] = useState('');
  const [error, setError] = useState<string | null>(null);

  const cargar = async () => {
    const res = await window.api.zonas.listar();
    if (res.ok) setZonas(res.data);
    setCargando(false);
  };

  useEffect(() => {
    void cargar();
  }, []);

  const agregar = async () => {
    setError(null);
    const pesos = Number(tarifa.replace(',', '.'));
    if (nombre.trim().length < 2) {
      setError('Ponle el nombre con el que conoces la zona.');
      return;
    }
    if (!Number.isFinite(pesos) || pesos < 0) {
      setError('La tarifa tiene que ser un número.');
      return;
    }
    /**
     * Sprint 111 — El "desde" se encadena con la zona más lejana que ya exista.
     *
     * El negocio sólo escribe "hasta X km". Si ya tiene "hasta 3", la siguiente empieza en 3 — así
     * los rangos nunca se traslapan ni dejan huecos, y nadie tiene que pensar en intervalos.
     */
    const hasta = hastaKm.trim() === '' ? null : Number(hastaKm.replace(',', '.'));
    if (hasta !== null && (!Number.isFinite(hasta) || hasta <= 0)) {
      setError('Los kilómetros tienen que ser un número mayor que cero. Déjalo vacío si esta zona es "de ahí en adelante".');
      return;
    }
    const topeActual = zonas.reduce((max, z) => Math.max(max, z.kmHasta ?? z.kmDesde ?? 0), 0);
    const desde = hasta === null && topeActual === 0 ? null : topeActual;
    if (hasta !== null && desde !== null && hasta <= desde) {
      setError(`Ya tienes cubierto hasta ${topeActual} km. Esta zona tiene que llegar más lejos.`);
      return;
    }

    const res = await window.api.zonas.crear({
      nombre: nombre.trim(),
      tarifa: pesosACentavos(pesos),
      kmDesde: desde,
      kmHasta: hasta,
    });
    if (!res.ok) {
      setError(traducirErrorBackend(res.error));
      return;
    }
    setNombre('');
    setTarifa('');
    setHastaKm('');
    void cargar();
  };

  const quitar = async (z: ZonaEnvioVista) => {
    const ok = await window.confirmar({
      titulo: `Quitar "${z.nombre}"`,
      mensaje:
        'Deja de aparecer como atajo al cotizar. Los pedidos que ya se cobraron con esa tarifa no se tocan.',
      textoConfirmar: 'Quitarla',
      variant: 'warn',
    });
    if (!ok) return;
    const res = await window.api.zonas.eliminar(z.id);
    if (!res.ok) {
      setError(traducirErrorBackend(res.error));
      return;
    }
    void cargar();
  };

  return (
    <div className="card space-y-3">
      <div className="flex items-center gap-2">
        <MapPin className="h-4 w-4 text-slate-500" />
        <h3 className="font-semibold">Zonas y su tarifa</h3>
      </div>
      <p className="text-xs text-slate-500">
        Aparecen como botones al cotizar un envío, para no teclear el mismo monto cuarenta veces
        al mes. <b>No se aplican solas</b>: tú decides qué cobrar en cada pedido.
      </p>

      {cargando ? (
        <p className="text-sm text-slate-500">Cargando…</p>
      ) : zonas.length === 0 ? (
        <p className="rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-500">
          Todavía no hay zonas. Agrega las que ya conoces de memoria y las verás al cotizar.
        </p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {zonas.map((z) => (
            <li key={z.id} className="flex items-center justify-between gap-3 py-2">
              <span className="text-sm">
                {z.nombre}
                {/* Sprint 111 — Sin rango, la zona sigue siendo un atajo manual: no se elige sola. */}
                {z.kmDesde !== null && z.kmDesde !== undefined ? (
                  <span className="ml-2 text-xs text-slate-500">
                    {z.kmHasta != null ? `de ${z.kmDesde} a ${z.kmHasta} km` : `más de ${z.kmDesde} km`}
                  </span>
                ) : (
                  <span className="ml-2 text-xs text-amber-700">sólo atajo manual</span>
                )}
              </span>
              <span className="flex items-center gap-3">
                <span className="font-mono text-sm font-semibold tabular-nums">
                  {formatPrecio(z.tarifa, '$')}
                </span>
                <button
                  onClick={() => void quitar(z)}
                  className="btn-ghost px-2 py-1 text-xs text-rose-700"
                >
                  Quitar
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}

      <div className="flex flex-wrap items-end gap-2 border-t border-slate-200 pt-3">
        <div className="min-w-[160px] flex-1">
          <label className="label" htmlFor="zonaNombre">
            Zona
          </label>
          <input
            id="zonaNombre"
            className="input"
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            placeholder="Galaxia"
            maxLength={60}
          />
        </div>
        <div className="w-32">
          <label className="label" htmlFor="zonaTarifa">
            Cuánto cobras
          </label>
          <input
            id="zonaTarifa"
            className="input"
            inputMode="decimal"
            value={tarifa}
            onChange={(e) => setTarifa(e.target.value)}
            placeholder="50"
          />
        </div>
        {/*
          Sprint 111 — Un solo número por renglón: "hasta cuántos km".

          El "desde" se encadena solo con la zona anterior. En el piso nadie piensa "de 3 a 6 km":
          piensa *"hasta 3 cobro $40, hasta 6 cobro $50, más lejos cotizo"*. Pedir dos números por
          renglón —y antes, pedir colonias— es por lo que esta sección llevaba meses vacía.
        */}
        <div className="w-36">
          <label className="label" htmlFor="zonaHastaKm">
            Hasta cuántos km
          </label>
          <input
            id="zonaHastaKm"
            className="input"
            inputMode="decimal"
            value={hastaKm}
            onChange={(e) => setHastaKm(e.target.value)}
            placeholder="3"
          />
        </div>
        <button onClick={agregar} className="btn-primary min-h-[44px]">
          <Plus className="h-4 w-4" />
          Agregar
        </button>
      </div>

      <p className="text-xs text-slate-500">
        Escribe <b>hasta cuántos kilómetros</b> llega cada tarifa, de la más cercana a la más lejana:
        <i> hasta 3 km $40, hasta 6 km $50, hasta 10 km $70</i>. Deja el último vacío si de ahí en
        adelante prefieres cotizar. Con eso el sistema te <b>sugiere</b> el monto cuando el cliente
        comparte su ubicación — sugerirlo, no cobrarlo: el precio sigue siendo el que te dé el
        motomandado.
      </p>

      {error && (
        <div className="error-text" role="alert">
          {error}
        </div>
      )}
    </div>
  );
}
